<template>
  <Layout>
    <PageHeader :title="title" :items="items" />

    <div class="row" v-if="userData.permissions['view_documents'] == 1">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="mb-2 float-end">
              <router-link v-show="userData.permissions['add_documents'] == 1" to="/new-document"
                class="btn btn-sm btn-primary"><i class="mdi mdi-plus"></i> New Document</router-link>
            </div>
            <div class="clearfix"></div>

            <div class="row mb-3">
              <div class="col-sm-12 col-md-6">
                <div class="dataTables_length">
                  <label class="d-inline-flex align-items-center">
                    Show <b-form-select class="form-select form-select-sm mx-1" v-model="perPage" size="sm"
                      :options="Options"></b-form-select> entries
                  </label>
                </div>
              </div>
              <div class="col-sm-12 col-md-6">
                <div class="dataTables_filter text-md-end">
                  <label class="d-inline-flex align-items-center">
                    Search: <b-form-input v-model="filter" type="search" placeholder="Search..."
                      class="form-control form-control-sm ms-2"></b-form-input>
                  </label>
                </div>
              </div>
            </div>

            <!-- Documents List Loading State -->
            <div v-if="loadingDocuments" class="text-center text-primary my-4">
              <b-spinner class="align-middle" type="grow" small></b-spinner> <strong>Fetching documents...</strong>
            </div>

            <!-- Empty State -->
            <div v-else-if="paginatedItems.length === 0" class="text-center p-5 bg-light rounded border border-dashed">
              <div class="text-muted mb-3">
                <i class="display-4 mdi mdi-folder-open-outline"></i>
              </div>
              <h5 class="fw-bold text-dark mb-2">No Documents Yet</h5>
              <p class="text-muted mb-4 max-w-sm mx-auto">
                Documents are stored in the database. Add a new document to get started.
              </p>
              <router-link v-show="userData.permissions['add_documents'] == 1" to="/new-document"
                class="btn btn-primary">
                <i class="mdi mdi-plus me-1"></i> Add Document
              </router-link>
            </div>

            <!-- Documents Grid -->
            <div v-else class="row">
              <div class="col-xl-3 col-lg-4 col-md-6 mb-4" v-for="doc in paginatedItems" :key="doc.id">
                <div class="card shadow-sm rounded overflow-hidden border h-100"
                     :style="(!doc.status || doc.status.toLowerCase() === 'pending') ? 'border-top: 4px solid #50a5f1 !important;' : (doc.status && doc.status.toLowerCase() === 'approved' ? 'border-top: 4px solid #34c38f !important;' : '')">
                  <div
                    class="bg-light d-flex align-items-center justify-content-center border-bottom document-preview-area overflow-hidden p-0 position-relative thumbnail-container">
                    <img v-if="doc.thumbnail" :src="`${documentUrl}${doc.thumbnail}`" class="w-100 h-100"
                      style="object-fit: cover; object-position: top;" alt="Document Preview">

                    <div v-else class="text-center text-muted">
                      <i class="mdi mdi-file-pdf-box display-4 mb-2 d-block text-danger"></i>
                      <span class="fw-medium">No preview available</span>
                    </div>

                    <!-- Overlay for download -->
                    <a href="#" @click.prevent="downloadDocument(doc.id, doc.title)" class="thumbnail-overlay">
                      <i class="mdi mdi-download"></i>
                      <span>Click to download</span>
                    </a>
                  </div>

                  <div class="card-body p-3">
                    <div class="justify-content-between align-items-center flex-wrap gap-2">
                      <div class="d-flex flex-column flex-grow-1">
                        <h5 class="mb-0 fw-bold text-dark" :title="doc.title">{{
                          doc.title }}</h5>
                        <small @click="filter = doc.document_type" class="text-muted mt-1 hover-underline">
                          {{ doc.document_type || 'Unknown Type' }}
                        </small>
                      </div>

                      <div class="d-flex mt-3 justify-content-between align-items-center flex-wrap gap-2">
                        <div>
                          <b-badge class="px-3 py-1 rounded-pill"
                            :class="{ 'bg-primary': doc.status == 'pending', 'bg-success': doc.status == 'approved' }">
                            {{ doc.status || 'Pending' }}
                          </b-badge>
                        </div>

                        <div class="text-muted fw-medium small px-2">
                          {{ doc.created_at || 'YYYY-MM-DD' }}
                        </div>
                      </div>

                    </div>

                    <div class="mt-4 d-flex justify-content-between align-items-center">
                      <small class="text-muted"><i class="mdi mdi-download me-1"></i> {{ doc.downloads || 0 }}
                        Downloads</small>
                      <div class="gap-1 d-flex flex-wrap justify-content-end">
                        <b-button
                          v-show="userData.permissions['approve_documents'] == 1 && (!doc.status || doc.status.toLowerCase() == 'pending')"
                          @click="approve(doc.id, doc.title)" type="button" class="btn-sm btn" variant="success"
                          title="Approve"> <i class="mdi mdi-check"></i> </b-button>

                        <b-button
                          v-show="userData.permissions['approve_documents'] == 1 && (doc.status && doc.status.toLowerCase() == 'approved')"
                          @click="rollback(doc.id, doc.title)" type="button" class="btn-sm btn" variant="warning"
                          title="Rollback"> <i class="mdi mdi-undo"></i> </b-button>

                        <b-button v-show="userData.permissions['add_documents'] == 1 && (!doc.status || doc.status.toLowerCase() == 'pending')"
                          @click="edit(doc.id, doc.title, doc.document_type)" type="button" class="btn-sm btn"
                          variant="primary" title="Edit"> <i class="mdi mdi-square-edit-outline"></i> </b-button>

                        <b-button v-show="userData.permissions['delete_documents'] == 1 && (!doc.status || doc.status.toLowerCase() == 'pending')"
                          @click="remove(doc.id, doc.title)" type="button" class="btn-sm btn" variant="danger"
                          title="Delete"> <i class="mdi mdi-trash-can"></i> </b-button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="row" v-if="filteredItems.length > 0">
              <div class="col">
                <div class="dataTables_paginate paging_simple_numbers float-end">
                  <ul class="pagination pagination-rounded mb-0">
                    <b-pagination v-model="currentPage" :total-rows="TotalRows" :per-page="perPage"></b-pagination>
                  </ul>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>

    <div v-else>
      <div class="row justify-content-center">
        <div class="col-md-8 col-xl-6">
          <div>
            <img src="@/assets/images/profile-img.png" alt class="img-fluid" /><br><br>
            <center><span class="text-primary h6">You have no permission to view Company Documents</span></center>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit Modal -->
    <b-modal id="edit-document" centered title="Edit Document Details" title-class="fw-bold fs-5" hide-footer
      content-class="border-0 shadow-lg" header-class="border-bottom-0 pb-0" body-class="pt-3 p-4">
      <form @submit.prevent="updateDocument">

        <p class="text-muted small mb-4">
          Update the title and categorization of your document.
        </p>

        <div class="form-group mb-4">
          <label for="editDocumentTitle" class="fw-medium mb-2">
            Title <span class="text-danger">*</span>
          </label>
          <input id="editDocumentTitle" v-model="edit_title" type="text" class="form-control"
            placeholder="e.g., Q3 Financial Report" required :disabled="loading" />
        </div>

        <div class="form-group mb-4">
          <label for="editDocumentType" class="fw-medium mb-2">
            Document Type <span class="text-danger">*</span>
          </label>
          <b-form-select id="editDocumentType" v-model="edit_type" class="form-select" required :disabled="loading">
            <option value="" disabled>Select Document Type</option>
            <option v-for="type in documenttypes" :key="type.id" :value="type.id">
              {{ type.name }}
            </option>
          </b-form-select>
        </div>

        <div class="d-flex justify-content-end mt-4 pt-3 border-top">
          <b-button type="button" class="btn px-4" variant="light" @click="$bvModal.hide('edit-document')"
            :disabled="loading">
            Cancel
          </b-button>

          <b-button type="submit" class="btn ms-2 px-4 shadow-sm" variant="primary"
            :disabled="loading || !edit_title || !edit_type">
            <b-spinner small v-if="loading" class="me-2"></b-spinner>
            <template v-else>
              <i class="mdi mdi-content-save me-1"></i> Save Changes
            </template>
          </b-button>
        </div>
      </form>
    </b-modal>

    <!-- Approve Modal -->
    <b-modal id="approve-document" centered title="Approve Document" title-class="font-18" hide-footer>
      <form @submit.prevent="approveDocument">
        <div class="text-center mb-4 mt-2">
          <i class="mdi mdi-check-circle-outline display-4 text-success d-block mb-2"></i>
          <p class="mb-1 fs-5">
            Are you sure you want to approve the Document:<br>
            <strong class="text-dark">{{ title_action }}</strong>?
          </p>
          <hr>
          <small class="fw-medium">This will make the document visible to all the members.</small>

        </div>
        <div class="d-flex justify-content-center mt-4">
          <b-button type="button" class="btn px-4" variant="light"
            @click="$bvModal.hide('approve-document')">Cancel</b-button>
          <b-button type="submit" class="btn ms-2 px-4" variant="success">
            <b-spinner small v-if="loading"></b-spinner>
            <span v-else><i class="mdi mdi-check me-1"></i> Approve</span>
          </b-button>
        </div>
      </form>
    </b-modal>

    <!-- Rollback Modal -->
    <b-modal id="rollback-document" centered title="Rollback Document" title-class="font-18" hide-footer>
      <form @submit.prevent="rollbackDocument">
        <div class="text-center mb-4 mt-2">
          <i class="mdi mdi-undo-variant display-4 text-warning d-block mb-2"></i>
          <p class="mb-1 fs-5">
            Are you sure you want to rollback the Document:<br>
            <strong class="text-dark">{{ title_action }}</strong>?
          </p>
          <hr>
          <small class="fw-medium px-2">This action will revert the document to the pending state and it will no longer be visible to the members.</small>
        </div>
        <div class="d-flex justify-content-center mt-4">
          <b-button type="button" class="btn px-4" variant="light"
            @click="$bvModal.hide('rollback-document')">Cancel</b-button>
          <b-button type="submit" class="btn ms-2 px-4" variant="warning">
            <b-spinner small v-if="loading"></b-spinner>
            <span v-else><i class="mdi mdi-undo me-1"></i> Rollback</span>
          </b-button>
        </div>
      </form>
    </b-modal>

    <b-modal id="delete-document" centered title="Delete Document" title-class="font-18" hide-footer>
      <form @submit.prevent="deleteDocument">
        <input v-model="id" type="hidden">

        <div class="text-center mb-4 mt-2">
          <i class="mdi mdi-alert-circle-outline display-4 text-danger d-block mb-2"></i>
          <p class="mb-1 fs-5">
            Are you sure you want to delete the Document:<br>
            <strong class="text-dark">{{ title_action }}</strong>?
          </p>
          <small class="text-muted">This action cannot be undone.</small>
        </div>

        <div class="d-flex justify-content-center mt-4">
          <b-button type="button" class="btn px-4" variant="light" @click="$bvModal.hide('delete-document')">
            Cancel
          </b-button>

          <b-button type="submit" class="btn ms-2 px-4" variant="danger">
            <b-spinner small v-if="loading"></b-spinner>
            <span v-else><i class="mdi mdi-delete-outline me-1"></i> Delete</span>
          </b-button>
        </div>
      </form>
    </b-modal>
  </Layout>
</template>

<script>
import axios from "axios";
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config";
import { documentMethods, documentComputed, documentTypeMethods, documentTypeComputed } from "@/state/helpers";

export default {
  page: {
    title: "Documents",
    meta: [{ name: "description", content: appConfig.description }],
  },
  components: {
    Layout, PageHeader
  },
  data() {
    return {
      userData: JSON.parse(localStorage.getItem("user")) || { permissions: {} },
      title: "Documents",
      items: [{ text: "Dashboard", to: "/" }, { text: "Documents", active: true }],
      currentPage: 1,
      perPage: 10,
      Options: [10, 50, 100, 150, 200],
      filter: null,
      id: '',
      title_action: '',
      edit_title: '',
      edit_type: '',
      loading: false,
      documentUrl: axios.defaults.baseURL.slice(0, -4)
    };
  },
  computed: {
    ...documentComputed,
    ...documentTypeComputed,
    Items() {
      return (this.documents || []).map((d) => ({ ...d }));
    },

    filteredItems() {
      if (!this.filter) return this.Items;
      const lowerFilter = this.filter.toLowerCase();
      return this.Items.filter(item => {
        return (item.title && item.title.toLowerCase().includes(lowerFilter)) ||
          (item.document_type && item.document_type.toLowerCase().includes(lowerFilter)) ||
          (item.status && item.status.toLowerCase().includes(lowerFilter));
      });
    },
    TotalRows() {
      return this.filteredItems.length;
    },
    paginatedItems() {
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      return this.filteredItems.slice(start, end);
    }
  },
  mounted() {
    this.fetchDocuments();
    this.fetchDocumentTypes();
  },
  watch: {
    filter() {
      this.currentPage = 1;
    }
  },
  methods: {
    ...documentMethods,
    ...documentTypeMethods,
    edit(id, title, document_type) {
      this.id = id;
      this.edit_title = title;
      
      let matchedType = this.documenttypes.find(t => t.name === document_type || t.id === document_type);
      this.edit_type = matchedType ? matchedType.id : (document_type || '');
      
      this.$bvModal.show('edit-document');
    },
    approve(id, title) {
      this.id = id;
      this.title_action = title;
      this.$bvModal.show('approve-document');
    },
    rollback(id, title) {
      this.id = id;
      this.title_action = title;
      this.$bvModal.show('rollback-document');
    },
    remove(id, title) {
      this.id = id;
      this.title_action = title;
      this.$bvModal.show('delete-document');
    },
    async updateDocument() {
      this.loading = true;
      let payload = {
        document_id: this.id,
        title: this.edit_title,
        document_type: this.edit_type
      };
      let url = "/update-document";
      axios.post(url, payload).then((response) => {
        if (response.status == 200) {
          this.fetchDocuments();
          this.$bvModal.hide('edit-document');
          this.$toast.success("Document Updated");
          this.loading = false;
        } else {
          this.loading = false;
          this.$toast.error(response?.data?.message || response?.data?.error || "An error occurred");
        }
      }).catch((error) => {
        this.loading = false;
        this.$toast.error(error?.response?.data?.message || error?.response?.data?.error || "An error occurred");
      });
    },
    async approveDocument() {
      this.loading = true;
      let payload = { document_id: this.id };
      let url = "/approve-document";
      axios.post(url, payload).then((response) => {
        if (response.status == 200) {
          this.fetchDocuments();
          this.$bvModal.hide('approve-document');
          this.$toast.success("Document Approved");
          this.loading = false;
        } else {
          this.loading = false;
          this.$toast.error(response?.data?.message || response?.data?.error || "An error occurred");
        }
      }).catch((error) => {
        this.loading = false;
        this.$toast.error(error?.response?.data?.message || error?.response?.data?.error || "An error occurred");
      });
    },
    async rollbackDocument() {
      this.loading = true;
      let payload = { document_id: this.id };
      let url = "/rollback-document";
      axios.post(url, payload).then((response) => {
        if (response.status == 200) {
          this.fetchDocuments();
          this.$bvModal.hide('rollback-document');
          this.$toast.success("Document Rolled Back");
          this.loading = false;
        } else {
          this.loading = false;
          this.$toast.error(response?.data?.message || response?.data?.error || "An error occurred");
        }
      }).catch((error) => {
        this.loading = false;
        this.$toast.error(error?.response?.data?.message || error?.response?.data?.error || "An error occurred");
      });
    },
    async deleteDocument() {
      this.loading = true;
      let payload = { document_id: this.id };
      let url = "/delete-document";
      axios.post(url, payload).then((response) => {
        if (response.status == 200) {
          this.fetchDocuments();
          this.$bvModal.hide('delete-document');
          this.$toast.success("Document Deleted");
          this.loading = false;
        } else {
          this.loading = false;
          this.$toast.error(response?.data?.message || response?.data?.error || "An error occurred");
        }
      }).catch((error) => {
        this.loading = false;
        this.$toast.error(error?.response?.data?.message || error?.response?.data?.error || "An error occurred");
      });
    },
    async downloadDocument(id, docTitle) {
      try {
        // 1. Make the POST request, but tell Axios to expect a blob directly
        const response = await axios.post('/download-document', {
          document_id: id
        }, {
          responseType: 'blob' // CRITICAL: This tells Axios we are receiving a file
        });

        if (response.status === 200) {
          // 2. Create the blob URL directly from the first response
          const url = window.URL.createObjectURL(new Blob([response.data]));
          this.$toast.success("Your Document is downloading...");

          const link = document.createElement('a');
          link.href = url;

          // Use the docTitle passed into the function, fallback to a default
          link.setAttribute('download', `${docTitle || 'document'}.pdf`);

          document.body.appendChild(link);
          link.click();

          // Cleanup
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);

          this.fetchDocuments(); // Refresh list to update download count
        }
      } catch (error) {
        // Note: Because responseType is 'blob', reading error JSON is slightly different
        if (error.response && error.response.data instanceof Blob) {
          const errorText = await error.response.data.text();
          const errorJson = JSON.parse(errorText);
          this.$toast.error(errorJson.message || "Failed to download document");
        } else {
          this.$toast.error("An error occurred");
        }
      }
    },
  },
};
</script>

<style scoped>
.document-preview-area {
  height: 220px;
  background-color: #f8f9fa;
}

.thumbnail-container {
  position: relative;
}

.hover-underline:hover {
  cursor: pointer;
  text-decoration: underline;
}

.thumbnail-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  opacity: 0;
  transition: opacity 0.3s ease;
  text-decoration: none;
  z-index: 2;
}

.thumbnail-overlay i {
  font-size: 40px;
  margin-bottom: 10px;
  color: white;
}

.thumbnail-overlay span {
  font-weight: 500;
  color: white;
}

.thumbnail-container:hover .thumbnail-overlay {
  opacity: 1;
}
</style>
